/**
 * Room cards, built for change (CHANGE 2026-10-18 §3.4; format of the prototype, room-card change of
 * 18 Oct 2026 — prototype `docs/prototype/papounan-reservation.html`).
 *
 * 1. cardModels(): plain "card view models", one per room — content, facts, amenities, rates (prices, tags,
 *    saving, « cheapest » as the API gave them: codes + values), availability and selection state. No markup.
 * 2. renderCard(): small part renderers placed in the order of the layout list (admin « Affichage »). The
 *    photos go in the left column, the rates under both columns, every other part in the information column.
 * 3. The first N amenities (« Affichage › Nombre d'équipements affichés », 0.9.3; default 3 since 1.9.3) on desktop
 *    and phone; the others stay in the DOM, hidden, until « Afficher plus ».
 * 5. Results declutter (1.9.0, change of 22 Oct 2026): the room's « atout » chip in the facts row; rate boxes = name
 *    (+ « Meilleur prix »), the conditions on one line, total | « Choisir », « Conditions complètes » — every box
 *    open at every width; an unavailable room = one line (renderOffLine).
 * 4. Every French text (room content, amenity titles, photo alt texts, Beds24 condition texts, rate names) comes
 *    from the page HTML (texts.js and the labels, translated server-side by TranslatePress), never from the API
 *    JSON (no-dynamic-translation change, 19 Oct 2026): a text missing from the page is left out.
 *
 * No business logic (invariant 1): the browser never computes a price, a saving or a ranking. « Meilleur prix »
 * (`cheapest`) comes from /search (the « −X % » saving was removed in 0.9.8); the preferred rate is the first one in the
 * admin order (presentation, not price).
 *
 * Stable CSS classes per part: docs/handoff/customisation.md.
 */
import { h, html, clear } from './dom.js';
import { rateSummary, cancelText, paymentText } from './rate-summary.js';

export { rateSummary };
import { NO_TEXTS } from './texts.js';

export const DEFAULT_LAYOUT = ['photos', 'badge', 'title', 'slogan', 'facts', 'description', 'amenities', 'rates'];

/** Phone layout of the card (rates stacked). */
export const PHONE = '(max-width: 760px)';

/** Amenities shown before « Afficher plus » when the setting is missing (DisplaySettings::AMENITIES_DEFAULT, 1.9.3). */
export const AMENITIES_DEFAULT = 3;

/**
 * View models of the cards, in the order of /search `rooms` (decided by the server, 0.9.4: bookable rooms first,
 * then the others, each group in the /rooms order), joined to the /rooms content by roomId. A room without
 * content in /rooms gets no card.
 * @param {Object} state Booking state (rooms, search, chosen, shown, room).
 * @param {Object} display Admin display options.
 * @param {(offerId: number) => ?string} [rateName] Name printed in the page for a rate (« Affichage » display
 *   name or Beds24 name, translated server-side); null → no name (never the API's).
 * @param {Object} [texts] Page texts (texts.js readTexts()): room content, features, Beds24 condition texts.
 * @returns {Array<Object>}
 */
export function cardModels(state, display = {}, rateName = () => null, texts = NO_TEXTS) {
	const body = state.search?.status === 200 ? state.search.body : null;
	if (!body) {
		return [];
	}
	const contents = new Map(state.rooms.map((c) => [c.roomId, c]));
	const out = [];
	for (const result of body.rooms) {
		const content = contents.get(result.roomId);
		if (!content) {
			continue;
		}
		const chosen = state.chosen[content.roomId] || null;
		const options = result.occupancyOptions || [];
		const shown = state.shown[content.roomId] ?? options[0]?.occupancy ?? null;
		const option = options.find((o) => o.occupancy === shown) || options[0] || null;
		// Every photo /rooms sends (12 at most, 1.9.2): the arrows show them all; « Photos par carte » = thumbnails shown.
		const thumbs = display.photos ?? 5;
		const photos = (content.offerPhotos?.length ? content.offerPhotos : [content.heroImage, ...(content.gallery || [])].filter(Boolean))
			.slice(0, display.photoMode === 'single' ? Math.min(1, thumbs) : thumbs === 0 ? 0 : 12);
		const rates = option ? option.rates : []; // admin order, from the server (the first one is the preferred rate)
		// Page texts only (translated server-side); the API's French values are never shown.
		const room = (key) => texts.text('room.' + content.roomId + '.' + key) ?? '';
		out.push({
			roomId: content.roomId,
			name: room('name'),
			slogan: display.showSlogan === false ? '' : room('slogan'),
			short: display.showSlogan === false ? '' : room('short'),
			url: content.url || '',
			badge: room('badge'),
			facts: facts(content, display, room),
			amenities: amenities(content, texts),
			amenitiesShown: clampAmenities(display.amenitiesShown),
			photos: photos.map((p) => ({ ...p, alt: texts.text('photo.' + p.url) ?? '' })),
			photoMode: display.photoMode || 'gallery',
			thumbs,
			bookable: Boolean(result.bookable),
			reason: result.bookable ? null : result.reason,
			// The requested room (1.7.0): the server's echo (`requested`), so an unknown room_id highlights nothing.
			requested: body.requested?.roomId === content.roomId,
			highlighted: body.requested?.roomId === content.roomId,
			chosen: Boolean(chosen),
			occupancy: options.length > 1 ? { options: options.map((o) => o.occupancy), shown: option?.occupancy } : null,
			stay: body.stay || null,
			rates: rates.map((rate, i) => ({
				offerId: rate.offerId,
				name: rateName(rate.offerId) || '',
				descriptionHtml: texts.rateDesc(content.roomId, rate.offerId) ?? '',
				tags: rate.tags || [],
				perNight: rate.perNight,
				total: rate.total,
				totalBeforePromo: rate.totalBeforePromo ?? null,
				perNightBeforePromo: rate.perNightBeforePromo ?? null,
				promoNote: rate.promoNote ?? null, // PROMO_NOT_ON_RATE | PROMO_UNAVAILABLE_ONLINE: full price, a line says why
				cheapest: rate.cheapest === true,
				preferred: i === 0,
				chosen: chosen?.offerId === rate.offerId,
				summary: rateSummary(rate.tags || [])
			})),
			notes: (result.unavailableRates || []).map((r) => ({ name: rateName(r.offerId) || '', reason: r.reason })),
			ratesCollapsed: Boolean(display.ratesCollapsed),
			ctaLabel: display.ctaLabel || ''
		});
	}
	return out;
}

/**
 * Facts row: capacity (ACF capacite, else Beds24 maxPeople), m² (superficie), the room's « atout » (ACF atout,
 * 1.9.0: a highlighted chip), bedding (literie), floor (etage).
 */
function facts(content, display, room = () => '') {
	const out = [];
	const capacity = room('capacity') || (content.maxPeople ? String(content.maxPeople) : '');
	if (display.showCapacity !== false && capacity) {
		out.push({ kind: 'capacity', value: capacity });
	}
	if (content.sizeM2) {
		out.push({ kind: 'size', value: String(content.sizeM2) });
	}
	const atout = room('atout');
	if (atout) {
		out.push({ kind: 'atout', text: atout });
	}
	// One pill per bed (1.9.3): PHP splits the ACF literie on « / » and prints room.<id>.bed.<n>.
	for (let n = 1; room('bed.' + n); n++) {
		out.push({ kind: 'bed', text: room('bed.' + n) });
	}
	const floor = room('floor');
	if (floor) {
		out.push({ kind: 'floor', text: floor });
	}
	return out;
}

/** « Nombre d'équipements affichés »: 1–12, default 3 (the server validates it too). */
function clampAmenities(n) {
	const value = Number(n);
	return Number.isInteger(value) ? Math.max(1, Math.min(12, value)) : AMENITIES_DEFAULT;
}

/**
 * Amenities: the room's highlights (ACF points_forts) first, then its features (fonctionalites), no duplicates;
 * titles from the page only (room.<id>.hl.<n>, feature.<id>).
 */
function amenities(content, texts = NO_TEXTS) {
	const key = (text) => String(text).trim().toLowerCase();
	const seen = new Set();
	const out = [];
	const lines = (content.highlights || []).map((line, i) => texts.text('room.' + content.roomId + '.hl.' + (i + 1)));
	for (const line of lines) {
		if (line && !seen.has(key(line))) {
			seen.add(key(line));
			out.push({ label: line, icon: null });
		}
	}
	for (const f of content.features || []) {
		const label = texts.text('feature.' + f.id);
		if (label && !seen.has(key(label))) {
			seen.add(key(label));
			out.push({ label, icon: f.icon || null });
		}
	}
	return out;
}

/**
 * Display order of the rates: Beds24 order, or one offer first (presentation only — the browser never ranks
 * prices; « cheapest » before 0.9.0 is read as Beds24 order). Since 0.9.2 /search already returns the rates in
 * the admin order; kept for callers that sort other lists.
 */
export function orderRates(rates, order = 'beds24') {
	const list = [...rates];
	if (/^\d+$/.test(String(order))) {
		const first = Number(order);
		return list.sort((a, b) => (b.offerId === first) - (a.offerId === first));
	}
	return list;
}

/**
 * Renders one card.
 * @param {Object} model Card view model.
 * @param {{i18n: Object, msg: Object, layout?: string[], actions: {toggleRate: Function, setOccupancy: Function},
 *   open?: Set, more?: Set, first?: boolean, stale?: boolean}} ctx
 *   stale: results no longer match the form (0.9.7): « Choisir » and the 2 / 3 switch disabled;
 *   open: rooms whose collapsed rates (« Voir les tarifs ») are open; more: rooms whose information is expanded —
 *   both kept across re-renders.
 */
export function renderCard(model, ctx) {
	const layout = (ctx.layout?.length ? ctx.layout : DEFAULT_LAYOUT).filter((p) => p in PARTS);
	const expanded = Boolean(ctx.more?.has(model.roomId));
	const card = h('article', {
		class: ['pap-card', model.bookable ? '' : 'is-off', model.chosen ? 'is-chosen' : '', model.highlighted ? 'is-highlighted' : '', expanded ? 'is-more' : ''].filter(Boolean).join(' '),
		'data-room': String(model.roomId)
	});
	const top = h('div', { class: 'pap-card-top' });
	const body = h('div', { class: 'pap-card-body', id: 'pap-room-' + model.roomId });
	let rates = null;
	let media = null;
	for (const part of layout) {
		const node = PARTS[part](model, { ...ctx, expanded });
		if (!node) {
			continue;
		}
		if (part === 'photos') {
			media = node;
		} else if (part === 'rates') {
			rates = node;
		} else {
			body.append(node);
		}
	}
	if (media) {
		top.append(media);
	} else {
		card.classList.add('no-photos');
	}
	top.append(body);
	card.append(top);
	if (rates) {
		card.append(rates);
	}
	return card;
}

// ---------------------------------------------------------------- icons (static, decorative)

const SVG_NS = 'http://www.w3.org/2000/svg';
const ICONS = {
	capacity: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c1.4-3.6 4.4-5.5 8-5.5s6.6 1.9 8 5.5',
	size: 'M4 4h16v16H4zM4 9h5V4M15 20v-5h5',
	bed: 'M3 18V7M3 14h18v4M21 14v-2a3 3 0 0 0-3-3h-8v5M6.5 9.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z',
	floor: 'M4 20h16M6 20V10l6-5 6 5v10',
	atout: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z',
	check: 'M5 12.5l4.5 4.5L19 7',
	chevron: 'M6 9l6 6 6-6'
};

function icon(name, cls) {
	const svg = document.createElementNS(SVG_NS, 'svg');
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('aria-hidden', 'true');
	svg.setAttribute('focusable', 'false');
	svg.setAttribute('class', cls);
	const path = document.createElementNS(SVG_NS, 'path');
	path.setAttribute('d', ICONS[name]);
	svg.append(path);
	return svg;
}

// ---------------------------------------------------------------- parts (each returns a node or null)

const PARTS = {
	/**
	 * Main photo, its ‹ › arrows and counter (1.9.2: every photo of the room, swipe on a phone), then the first
	 * « Photos par carte » thumbnails — the last one shows « +N » when more photos follow. The photo shown is kept
	 * across re-renders (ctx.photo: room → index).
	 */
	photos(m, { i18n, first, photo }) {
		if (m.photos.length === 0) {
			return null;
		}
		const img = (p, cls) => h('img', { class: cls, src: p.url, alt: p.alt || '', width: p.width || null, height: p.height || null, loading: 'lazy', decoding: 'async' });
		const main = img(m.photos[0], 'pap-card-photo');
		if (first) {
			// First card: its main photo is the largest element of the results (LCP), load it at once.
			main.loading = 'eager';
			main.setAttribute('fetchpriority', 'high');
		}
		if (m.photoMode === 'single' || m.photos.length === 1) {
			return h('div', { class: 'pap-card-media' }, h('div', { class: 'pap-card-stage' }, main));
		}
		const n = m.photos.length;
		const shown = m.photos.slice(0, Math.min(m.thumbs, n));
		const count = h('span', { class: 'pap-card-count', 'aria-hidden': 'true' });
		const list = h('ul', { class: 'pap-card-thumbs', 'aria-label': i18n.s('ui.photos') });
		let at = 0;
		function go(i) {
			at = (i + n) % n;
			photo?.set(m.roomId, at);
			const p = m.photos[at];
			main.loading = 'eager';
			main.src = p.url;
			main.alt = p.alt || '';
			if (p.width && p.height) {
				main.width = p.width;
				main.height = p.height;
			}
			clear(count).append(i18n.value(String(at + 1)), i18n.sep(' / '), i18n.value(String(n)));
			list.querySelectorAll('.pap-card-thumb').forEach((b, k) => b.setAttribute('aria-pressed', String(k === at)));
		}
		const arrow = (dir) => h('button', {
			type: 'button',
			class: 'pap-card-arrow pap-card-arrow--' + (dir < 0 ? 'prev' : 'next'),
			'aria-label': i18n.s(dir < 0 ? 'ui.photoPrev' : 'ui.photoNext'),
			on: { click: () => go(at + dir) }
		}, icon('chevron', 'pap-card-arrow-icon'));
		shown.forEach((p, k) => {
			const more = k === shown.length - 1 && n > shown.length ? n - shown.length : 0;
			list.append(h('li', {}, h('button', {
				type: 'button',
				class: 'pap-card-thumb' + (more ? ' has-more' : ''),
				'aria-label': p.alt || i18n.s('ui.photos'),
				'aria-pressed': k === 0 ? 'true' : 'false',
				on: { click: () => go(k) }
			},
			h('img', { src: p.thumbUrl || p.url, alt: '', width: 150, height: 100, loading: 'lazy', decoding: 'async' }),
			more ? h('span', { class: 'pap-card-more-photos', 'aria-hidden': 'true' }, i18n.value('+' + more)) : null)));
		});
		const stage = h('div', { class: 'pap-card-stage' }, main, arrow(-1), arrow(1), count);
		// Swipe on a touch screen: a horizontal move of 40 px or more.
		let x0 = null;
		stage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
		stage.addEventListener('touchend', (e) => {
			const dx = x0 === null ? 0 : e.changedTouches[0].clientX - x0;
			x0 = null;
			if (Math.abs(dx) >= 40) {
				go(at + (dx < 0 ? 1 : -1));
			}
		}, { passive: true });
		go(photo?.get(m.roomId) ?? 0);
		if (at === 0 && !first) {
			main.loading = 'lazy';
		}
		return h('div', { class: 'pap-card-media' }, stage, shown.length > 1 ? list : null);
	},

	badge(m) {
		return m.badge ? h('p', { class: 'pap-card-badge' }, m.badge) : null;
	},

	title(m) {
		// A proper name (0.9.11): never translated, not even by TranslatePress's lookup of inserted text.
		return h('h3', { class: 'pap-card-title', 'data-no-translation': true }, m.name);
	},

	slogan(m) {
		return m.slogan ? h('p', { class: 'pap-card-slogan' }, m.slogan) : null;
	},

	facts(m, { i18n }) {
		if (m.facts.length === 0) {
			return null;
		}
		const text = (f) => (f.kind === 'capacity' ? i18n.t('ui.factCapacity', [f.value]) : f.kind === 'size' ? i18n.t('ui.factSize', [f.value]) : f.text);
		return h('ul', { class: 'pap-card-facts' }, ...m.facts.map((f) => h('li', { class: 'pap-card-fact pap-card-fact--' + f.kind }, icon(f.kind, 'pap-card-fact-icon'), h('span', {}, text(f)))));
	},

	/** Short description; expanded: the long one (room post content) and « Idéale pour » (ACF ideal_pour). */
	/** Short description only (1.9.3: « Afficher plus » no longer shows the long description or « Idéale pour »). */
	description(m) {
		return m.short ? h('div', { class: 'pap-card-intro' }, h('p', { class: 'pap-card-short' }, m.short)) : null;
	},

	/** Amenities (icon + full title, one per line) and the « Afficher plus » toggle of the information column. */
	amenities(m, ctx) {
		const { i18n, expanded } = ctx;
		const list = m.amenities.length
			? h('ul', { class: 'pap-card-amenities' }, ...m.amenities.map((a, i) => h('li', { class: 'pap-card-amenity', hidden: !expanded && i >= m.amenitiesShown },
				a.icon ? h('img', { class: 'pap-card-amenity-icon', src: a.icon, alt: '', width: 22, height: 22, loading: 'lazy', decoding: 'async' }) : icon('check', 'pap-card-amenity-icon'),
				h('span', {}, a.label))))
			: null;
		const page = m.url ? h('a', { class: 'pap-link pap-card-page', href: m.url, target: '_blank', rel: 'noopener', hidden: !expanded }, i18n.t('ui.roomPage')) : null;
		const toggle = h('button', {
			type: 'button',
			class: 'pap-link pap-card-more-btn',
			'aria-expanded': String(expanded),
			'aria-controls': 'pap-room-' + m.roomId,
			on: { click: (e) => expand(e.currentTarget.closest('.pap-card'), m, ctx) }
		}, i18n.t(expanded ? 'ui.showLess' : 'ui.showMore'));
		const wrap = h('div', { class: 'pap-card-amenity-box' }, list, h('div', { class: 'pap-card-more' }, toggle, page));
		return wrap;
	},

	rates(m, ctx) {
		const { i18n, msg } = ctx;
		if (!m.bookable) {
			return h('div', { class: 'pap-card-rates' }, h('p', { class: 'pap-card-unavailable pap-note pap-warn' }, i18n.t('ui.unavailableReason', [msg.reason('reason', m.reason)])));
		}
		// No « Tarifs pour 3 nuits · … » heading since 0.9.10: its space is kept (CSS, .pap-card-rates padding).
		const box = h('div', { class: 'pap-card-rates', id: 'pap-rates-' + m.roomId });
		if (m.occupancy) {
			box.append(occupancySwitch(m, ctx));
		}
		const list = h('div', { class: 'pap-rates', style: '--n:' + Math.max(m.rates.length, 2) });
		for (const rate of m.rates) {
			list.append(rateCard(m, rate, ctx));
		}
		box.append(list);
		for (const note of m.notes) {
			const node = rateNote(note, ctx);
			if (node) {
				box.append(node);
			}
		}
		if (!m.ratesCollapsed) {
			return box;
		}
		const shown = ctx.open?.has(m.roomId) || m.chosen;
		box.hidden = !shown;
		const toggle = h('button', {
			type: 'button',
			class: 'pap-btn pap-btn-ghost pap-card-toggle',
			'aria-expanded': String(shown),
			'aria-controls': box.id,
			on: {
				click: () => {
					box.hidden = !box.hidden;
					if (box.hidden) {
						ctx.open?.delete(m.roomId);
					} else {
						ctx.open?.add(m.roomId);
					}
					toggle.setAttribute('aria-expanded', String(!box.hidden));
					clear(toggle).append(i18n.t(box.hidden ? 'ui.seeRates' : 'ui.hideRates'));
				}
			}
		}, i18n.t(shown ? 'ui.hideRates' : 'ui.seeRates'));
		return h('div', { class: 'pap-card-ratebox' }, toggle, box);
	}
};

/** « Afficher plus / moins »: expands the information column in place. */
function expand(card, m, ctx) {
	const on = !card.classList.contains('is-more');
	if (on) {
		ctx.more?.add(m.roomId);
	} else {
		ctx.more?.delete(m.roomId);
	}
	card.classList.toggle('is-more', on);
	card.querySelector('.pap-card-page')?.toggleAttribute('hidden', !on);
	card.querySelectorAll('.pap-card-amenity').forEach((li, i) => { li.hidden = !on && i >= m.amenitiesShown; });
	const toggle = card.querySelector('.pap-card-more-btn');
	toggle.setAttribute('aria-expanded', String(on));
	clear(toggle).append(ctx.i18n.t(on ? 'ui.showLess' : 'ui.showMore'));
}

function occupancySwitch(m, { i18n, actions, stale = false }) {
	const group = h('fieldset', { class: 'pap-card-occupancy' }, h('legend', {}, i18n.t('ui.occupancyLegend')));
	for (const occ of m.occupancy.options) {
		group.append(h('label', {},
			// Stale results (0.9.7): the switch changes the chosen rate, so it is disabled like « Choisir ».
			h('input', { type: 'radio', name: 'pap-occ-' + m.roomId, checked: occ === m.occupancy.shown, disabled: stale || null, on: { change: () => actions.setOccupancy(m.roomId, occ) } }),
			i18n.t('ui.occupancy', [occ], occ)));
	}
	return group;
}

/**
 * One rate box (1.9.0, results declutter change of 22 Oct 2026), the same at every width (side by side on desktop,
 * stacked on a phone, always open): name, the conditions on one line
 * (cancellation · payment, the tag codes' texts) with the « Conditions complètes » chevron (1.9.1), the guest's code
 * note; a line; total (the total before a promo and the price per night under it) | « Choisir ». No « Meilleur prix »
 * since 1.9.1 (Jacques: distracting); the server's `cheapest` stays in the model and as `.is-cheap`.; « Conditions complètes ». No ribbon on the border, no breakfast line.
 */
function rateCard(m, rate, ctx) {
	const stayNights = m.stay?.nights ?? 0;
	const { i18n, actions, msg, stale = false } = ctx;
	const ids = 'pap-rate-' + m.roomId + '-' + rate.offerId;
	const sum = rate.summary;
	// Cancellation · payment: the date as Intl formats it in the page language (day + short month), never wrapped.
	const parts = [cancelText(i18n, sum.cancel), paymentText(i18n, sum.payment)].filter(Boolean);
	const conditions = parts.length ? h('span', { class: 'pap-rate-cond' }, ...parts.flatMap((p, i) => (i ? [i18n.sep(' · '), p] : [p]))) : null;
	const desc = rate.descriptionHtml ? html('div', 'pap-rate-desc', rate.descriptionHtml) : null;
	if (desc) {
		desc.hidden = true;
	}
	// « Conditions complètes » (1.9.1): a ˅ / ˄ chevron at the right end of the conditions line, its catalogue label as
	// the accessible name; the Beds24 text opens at the bottom of the box.
	const more = desc ? h('button', {
		type: 'button',
		class: 'pap-rate-more',
		'aria-expanded': 'false',
		'aria-controls': ids + '-d',
		'aria-label': i18n.s('ui.readMore'),
		title: i18n.s('ui.readMore'),
		on: {
			click: (e) => {
				desc.hidden = !desc.hidden;
				const button = e.currentTarget;
				button.setAttribute('aria-expanded', String(!desc.hidden));
				button.setAttribute('aria-label', i18n.s(desc.hidden ? 'ui.readMore' : 'ui.readLess'));
				button.title = button.getAttribute('aria-label');
			}
		}
	}, icon('chevron', 'pap-rate-more-icon')) : null;
	if (desc) {
		desc.id = ids + '-d';
	}
	return h('div', {
		class: ['pap-rate', rate.preferred ? 'is-pref' : '', rate.cheapest ? 'is-cheap' : '', rate.chosen ? 'is-chosen' : ''].filter(Boolean).join(' '),
		'data-offer': String(rate.offerId)
	},
	h('div', { class: 'pap-rate-h' },
		h('span', { class: 'pap-rate-name', id: ids + '-n' }, h('span', { class: 'pap-rate-title' }, rate.name)),
		conditions || more ? h('span', { class: 'pap-rate-cond-row' }, conditions, more) : null,
		desc), // the Beds24 text the chevron opens, above the line (1.9.2)
	h('div', { class: 'pap-rate-b' },
		h('div', { class: 'pap-rate-quick' },
			h('span', { class: 'pap-rate-price' },
				// Per night large (+ « / nuit »), the night price before a code struck next to it; the stay total small under (1.9.7).
				h('span', { class: 'pap-rate-main' },
					h('span', { class: 'pap-rate-night' }, i18n.value(i18n.money(rate.perNight)), ' ', h('span', { class: 'pap-rate-unit' }, i18n.t('ui.perNightSuffix'))),
					rate.perNightBeforePromo ? h('s', { class: 'pap-rate-before' }, i18n.value(i18n.money(rate.perNightBeforePromo))) : null),
				h('span', { class: 'pap-rate-total' }, i18n.t('ui.stayTotalNights', [stayNights, i18n.money(rate.total)], stayNights)),
				// The guest's code on this rate (server code): not ticked on it, or not usable online yet — full price.
				// In the price part, under the price per night (1.9.4).
				rate.promoNote ? h('span', { class: 'pap-rate-promo pap-warn' }, rate.promoNote === 'PROMO_NOT_ON_RATE'
					? i18n.t('ui.promoNotOnRate')
					: msg.withContact('promo.UNAVAILABLE_ONLINE')) : null),
			// « Affichage » label of the choose button (ctaLabel), else « Choisir » (0.9.8). The server's cheapest rate:
			// « Meilleur prix » in a bubble on hover and keyboard focus, and for screen readers (1.9.2).
			h('span', { class: 'pap-rate-pick-wrap' },
				h('button', {
					type: 'button',
					class: 'pap-btn pap-rate-pick',
					'aria-pressed': rate.chosen ? 'true' : 'false',
					'aria-describedby': ids + '-n' + (rate.cheapest ? ' ' + ids + '-tip' : ''),
					disabled: stale || null, // stale results: nothing can be chosen until « Rechercher » (0.9.7)
					on: { click: () => actions.toggleRate(m.roomId, rate.offerId) }
				}, rate.chosen ? i18n.t('ui.chosen') : m.ctaLabel ? m.ctaLabel : i18n.t('ui.chooseShort')),
				rate.cheapest ? h('span', { class: 'pap-tip', id: ids + '-tip', role: 'tooltip' }, i18n.t('ui.ribbonCheapest')) : null))));
}

/**
 * An unavailable room (1.9.0): one line under « Indisponibles pour ces dates » — a small thumbnail, the name, the
 * reason of the explanation layer (danger colour) and « Voir la chambre » (the room page). No gallery, tagline,
 * facts, description or amenities.
 */
export function renderOffLine(m, { i18n, msg }) {
	const photo = m.photos[0];
	return h('article', { class: ['pap-off', m.highlighted ? 'is-highlighted' : ''].filter(Boolean).join(' '), 'data-room': String(m.roomId) },
		photo
			? h('img', { class: 'pap-off-thumb', src: photo.thumbUrl || photo.url, alt: '', width: 84, height: 56, loading: 'lazy', decoding: 'async' })
			: h('span', { class: 'pap-off-thumb', 'aria-hidden': 'true' }),
		h('span', { class: 'pap-off-text' },
			// A proper name (0.9.11): never translated.
			h('span', { class: 'pap-off-name', 'data-no-translation': true }, m.name),
			h('span', { class: 'pap-off-reason' }, msg.reason('reason', m.reason))),
		m.url ? h('a', { class: 'pap-link pap-off-link', href: m.url, target: '_blank', rel: 'noopener' }, i18n.t('ui.seeRoom')) : null);
}

/** One line for a rate not bookable for these dates (server code + catalogue template). */
function rateNote(note, { i18n, msg }) {
	if (note.reason?.code === 'RATE_SUSPENDED') {
		// Beds24 prices it, the site does not sell it online (Pricing Phase 1): the name and the phone.
		const phone = msg.phoneLink();
		return h('p', { class: 'pap-rate-note pap-rate-suspended' }, phone
			? i18n.t('ui.rateNoteSuspendedFor', [note.name, phone])
			: i18n.t('ui.rateNoteSuspendedFor_NO_CONTACT', [note.name]));
	}
	const key = { RATE_LEAD_TIME: 'ui.rateNoteLeadTimeFor', MIN_STAY: 'ui.rateNoteMinStayFor', MAX_STAY: 'ui.rateNoteMaxStayFor' }[note.reason?.code];
	if (!key) {
		return null;
	}
	// One sentence per language (0.9.3): the name is a placeholder, so each language sets its own punctuation.
	const value = note.reason.days ?? note.reason.nights;
	return h('p', { class: 'pap-rate-note' }, i18n.t(key, [note.name, value], value));
}
